<template>
    <v-btn-toggle
        :value="value"
        mandatory
        dense
        @change="$emit('change', $event)"
    >
        <v-tooltip
            max-width="250"
            :disabled="!draft_inactive_reason"
            bottom
        >
            <template #activator="{ on, attrs }">
                <div
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-btn
                        value="pending"
                        :disabled="!!draft_inactive_reason"
                    >
                        Draft ({{ draft_count_display }})
                    </v-btn>
                </div>
            </template>
            <span>{{ draft_inactive_reason }}</span>
        </v-tooltip>

        <v-tooltip
            max-width="250"
            :disabled="!filtered_inactive_reason"
            bottom
        >
            <template #activator="{ on, attrs }">
                <div
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-btn
                        value="filtered"
                        :disabled="!!filtered_inactive_reason"
                    >
                        Filtered ({{ filtered_count_display }})
                    </v-btn>
                </div>
            </template>
            <span>{{ filtered_inactive_reason }}</span>
        </v-tooltip>

        <v-tooltip
            max-width="250"
            :disabled="!ready_to_send_inactive_reason"
            bottom
        >
            <template #activator="{ on, attrs }">
                <div
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-btn
                        value="ready_to_send"
                        :disabled="!!ready_to_send_inactive_reason"
                    >
                        Ready to Send ({{ ready_to_send_count_display }})
                    </v-btn>
                </div>
            </template>
            <span>{{ ready_to_send_inactive_reason }}</span>
        </v-tooltip>

        <v-tooltip
            max-width="250"
            :disabled="!sent_inactive_reason"
            bottom
        >
            <template #activator="{ on, attrs }">
                <div
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-btn
                        value="sent"
                        :disabled="!!sent_inactive_reason"
                    >
                        Sent ({{ sent_count_display }})
                    </v-btn>
                </div>
            </template>
            <span>{{ sent_inactive_reason }}</span>
        </v-tooltip>

        <!-- temp conditional
            TODO: add back :disabled="!failed_inactive_reason" to the tooltip
            TODO: add back {{ failed_inactive_reason }} in tooltip span
        -->
        <v-tooltip
            v-if="$rc.$state.isAuthActorSystemAdmin()"
            max-width="250"
            bottom
        >
            <template #activator="{ on, attrs }">
                <div
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-btn
                        value="failed"
                        class="admin-only"
                        :disabled="!!failed_inactive_reason"
                    >
                        Send Failed ({{ failed_count_display }})
                    </v-btn>
                </div>
            </template>
            <span>Staff Only</span>
        </v-tooltip>

        <v-tooltip
            v-if="cancelled_count_display"
            max-width="250"
            :disabled="!cancelled_inactive_reason"
            bottom
        >
            <template #activator="{ on, attrs }">
                <div
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-btn
                        value="cancelled"
                        :disabled="!!cancelled_inactive_reason"
                    >
                        Cancelled ({{ cancelled_count_display }})
                    </v-btn>
                </div>
            </template>
            <span>{{ cancelled_inactive_reason }}</span>
        </v-tooltip>
    </v-btn-toggle>
</template>

<script>
import { getInactiveReason, RECIPIENT_STATES } from '@/js/bulk/bulk-states.js';

export default {
    name: 'RecipientFilterControl',

    props: {
        value: {
            type: String,
            required: true,
        },
        message_state: {
            type: String,
            required: true,
        },
        progress_detail: {
            type: Object,
            required: false,
        },
    },

    computed: {
        draft_inactive_reason() {
            return getInactiveReason(this.message_state, RECIPIENT_STATES.DRAFT);
        },

        draft_count_display() {
            const count = this.progress_detail?.count_in_state.find(
                (item) => item.state === 'pending'
            )?.count_in_state || 0;

            return count.toLocaleString();
        },

        filtered_inactive_reason() {
            return getInactiveReason(this.message_state, RECIPIENT_STATES.FILTERED);
        },

        filtered_count_display() {
            const count = this.progress_detail?.count_in_state.find(
                (item) => item.state === 'filtered'
            )?.count_in_state || 0;

            return count.toLocaleString();
        },

        ready_to_send_inactive_reason() {
            return getInactiveReason(this.message_state, RECIPIENT_STATES.READY_TO_SEND);
        },

        ready_to_send_count_display() {
            const count = this.progress_detail?.count_in_state.find(
                (item) => item.state === 'ready_to_send'
            )?.count_in_state || 0;

            return count.toLocaleString();
        },

        sent_inactive_reason() {
            return getInactiveReason(this.message_state, RECIPIENT_STATES.SENT);
        },

        sent_count_display() {
            const count = this.progress_detail?.count_in_state.find(
                (item) => item.state === 'sent'
            )?.count_in_state || 0;

            return count.toLocaleString();
        },

        failed_inactive_reason() {
            return getInactiveReason(this.message_state, RECIPIENT_STATES.FAILED);
        },

        failed_count_display() {
            const count = this.progress_detail?.count_in_state.find(
                (item) => item.state === 'failed'
            )?.count_in_state || 0;

            return count.toLocaleString();
        },

        cancelled_inactive_reason() {
            return getInactiveReason(this.message_state, RECIPIENT_STATES.CANCELLED);
        },

        cancelled_count_display() {
            const count = this.progress_detail?.count_in_state.find(
                (item) => item.state === 'cancelled'
            )?.count_in_state || 0;

            return count > 0 ? count.toLocaleString() : null;
        },
    },
}
</script>

<style lang="scss" scoped>
.v-btn.v-btn--disabled.v-btn--has-bg {
    background-color: #f5f5f5 !important;
}

/* placing buttons within tooltips breaks v-btn-toggle default styling. rolling some of our own */
.v-item-group {
    border-radius: 4px !important;

    &>div:first-child button {
        border-top-left-radius: 4px !important;
        border-bottom-left-radius: 4px !important;
    }

    &>div:not(:first-child) button {
        border-left-width: 0 !important;
    }

    &>div:last-child button {
        border-top-right-radius: 4px !important;
        border-bottom-right-radius: 4px !important;
    }

    button {
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
        border-color: rgba(0, 0, 0, 0.12) !important;
        min-width: 48px;
        min-height: 0;
        padding: 0 8px;
        box-shadow: none !important;
        border-radius: 0;
        border-style: solid;
        border-width: thin;
        opacity: .8;
        padding: 0 12px;

        &.v-item--active {
            color: rgba(0, 0, 0, 0.87) !important;
        }

        // specificity wars
        &.v-btn.v-btn.admin-only {
            border-right: 3px solid #e53935 !important;
            font-weight: 500;
        }
    }
}
</style>